<template>
    <div v-if="activeTeamId">
        <UserOnboardingProgressBar v-if="showProgressBar" class="header-setting-btn dropdown mr-4"/>

        <AppsConnectionsPopover class="d-inline-block">
            <template v-slot:trigger="{ connections }">
                <div v-if="connections.length !== 0"
                     v-tooltip="`${connections.length} connection need attention`"
                     class="header-setting-btn mr-4">
                    <i class="fal fa-plug"></i>
                    <span class="invalid-connections-badge">
                        <i class="fa fa-exclamation-triangle"></i>
                    </span>
                </div>
            </template>
        </AppsConnectionsPopover>

        <div v-if="isStaff"
             v-tooltip.top="'Marketplace'"
             @click="openMarketplace"
             class="header-setting-btn mr-4">
            <i class="fal fa-puzzle-piece"></i>
        </div>

        <div class="header-setting-btn mr-4">
            <router-link :to="{ name: 'inbox' }"
                         v-tooltip.top="'Team Inbox'"
                         class="position-relative">
                <i class="fal fa-inbox"></i>
            </router-link>
        </div>

        <!--        <div class="header-setting-btn mt-2 mr-4">-->
        <!--            <router-link :to="{ name: 'notifications' }"-->
        <!--                         v-tooltip.top="'Notifications'"-->
        <!--                         class="position-relative">-->
        <!--                <i class="fal fa-bell"></i>-->
        <!--                <span v-show="notificationsCount !== 0" class="unread-notifications-count-badge">-->
        <!--                    {{ notificationsCount >= 100 ? '99+' : notificationsCount }}-->
        <!--                </span>-->
        <!--            </router-link>-->
        <!--        </div>-->

        <div class="d-inline-block dropdown mr-4">
            <a class="py-0"
               id="teamSettingsDropdown"
               href="#"
               role="button"
               data-toggle="dropdown"
               aria-haspopup="true"
               aria-expanded="false">
                <div class="position-relative pr-3" style="height: 24px; width: 24px">
                    <AppTeamLogo :team-id="activeTeamId"/>
                </div>
            </a>

            <div class="dropdown-menu dropdown-menu-right"
                 :style="showTeamSearch ? 'width: 400px !important': 'width: auto'"
                 aria-labelledby="teamSettingsDropdown">

                <h6 class="dropdown-header mb-0">
                    Teams
                    <span v-if="isStaff" @click="createTeam" class="pl-1 clickable text-primary-light">
                        <small><i class="fal fa-plus"></i></small>
                    </span>
                </h6>

                <div v-if="showTeamSearch" class="px-3 py-1">
                    <input
                        v-model="teamSearchString"
                        type="search"
                        placeholder="Search team"
                        class="form-control form-control-sm">
                </div>
                <div class="hide-scrollbar">
                    <div v-if="filteredTeamsList.length === 0">
                        <div class="dropdown-item text-muted">
                            No teams found
                        </div>
                    </div>
                    <div v-else class="teams-list">
                        <a v-for="(team, idx) in filteredTeamsList" :key="team.uuid + idx"
                           @click.prevent="switchTeam(team)"
                           class="dropdown-item team-name"
                           :class="{ 'text-bold': team.uuid === activeTeamId }">
                            <div class="d-flex align-items-center">
                                <div class="mr-2">
                                    <AppTeamLogo :team-id="team.uuid" :size="24"/>
                                </div>
                                <div v-tooltip="{content: team.name, delay: 500}"
                                     class="flex-grow-1 align-self-center overflow-ellipsis">
                                    {{ team.name }}
                                </div>
                                <div v-tooltip="'Pending documents'"
                                     class="d-flex align-self-center team-count text-muted font-14">
                                    {{ getTeamStats(team.uuid) }}
                                </div>
                            </div>
                        </a>
                    </div>
                </div>

                <div class="dropdown-divider"></div>
                <router-link class="dropdown-item"
                             :to="{ name: 'settings', params: { page: SETTINGS_USER_PROFILE }}">
                    <div class="menu-icon-container">
                        <i class="fal fa-cogs"></i>
                    </div>
                    Settings
                </router-link>
                <a @click.prevent="supportModal" class="dropdown-item" href="#">
                    <div class="menu-icon-container">
                        <i class="fal fa-question-circle"></i>
                    </div>
                    Help
                </a>
                <a @click.prevent="suggestionModal" class="dropdown-item" href="#">
                    <div class="menu-icon-container">
                        <i class="fal fa-lightbulb"></i>
                    </div>
                    Suggestions
                </a>
                <a v-if="$breakpoint.mdAndUp"
                   @click.prevent="$bus.$emit('OPEN_HOTKEYS')" class="dropdown-item" href="#">
                    <div class="menu-icon-container">
                        <i class="fal fa-keyboard"></i>
                    </div>
                    Keyboard shortcuts
                </a>
                <a class="dropdown-item" href="#" @click.prevent="logout">
                    <div class="menu-icon-container">
                        <i class="fal fa-sign-out"></i>
                    </div>
                    Logout
                </a>
            </div>
        </div>

    </div>
</template>

<script>
import {mapGetters, mapMutations} from 'vuex'
import CreateTeamModal from '../../modals/settings/createTeam.vue'
import CreateInvoiceModal from '../../views/documents/single/upload/CreateDocument.vue'
import SuggestionModal from '../../modals/settings/suggestion.vue'
import SupportModal from '../../modals/settings/support.vue'
import {AUTH_LOGOUT, AUTH_SET_ACTIVE_TEAM} from '@/store/mutations'
import {SETTINGS_DEMO_MARKETSHARE_AWARDS, SETTINGS_USER_PROFILE} from '@/views/settings/settings-tabs'
import UserOnboardingProgressBar from './UserOnboardingProgressBar'
import {AutomationTypes} from '@/types/automations/types'
import AppsConnectionsPopover from '../../views/apps/connections/all/AppsConnectionsPopover'
import AppTeamLogo from '../shared/AppTeamLogo'
import {sortTeamsDefault} from '@/utils/teams'

export default {
    name: 'HeaderAppSettings',
    components: {
        AppTeamLogo,
        AppsConnectionsPopover,
        UserOnboardingProgressBar
    },
    data() {
        return {
            teamSearchString: '',
            prevActiveTeamUuid: null,
            isSameSessionWhenOnboardingStepsPassed: false,
            SETTINGS_USER_PROFILE,
            notificationsCount: 0
        }
    },
    computed: {
        ...mapGetters([
            'activeTeam',
            'activeTeamId',
            'getUser',
            'getTeams',
            'permissions',
            'isAdminOrStaff',
            'isStaff',
            'teamStats',
            'hasAllOnboardingStepsPassed'
        ]),

        teamsList() {
            return sortTeamsDefault(this.getTeams)
        },

        filteredTeamsList() {
            return this.teamsList.filter(team => team.name.toLowerCase().includes(this.teamSearchString.toLowerCase()))
        },

        showProgressBar() {
            return this.$breakpoint.mdAndUp && !this.isWebExpensesApp && !this.activeTeam.isPartner &&
                (this.isSameSessionWhenOnboardingStepsPassed || !this.hasAllOnboardingStepsPassed)
        },

        showTeamSearch() {
            return this.teamsList.length > 5
        }
    },
    methods: {
        ...mapMutations({
            setActiveTeam: AUTH_SET_ACTIVE_TEAM
        }),

        getTeamStats(teamUuid) {
            const stats = this.teamStats.find(el => el.uuid === teamUuid)
            return stats && stats.total ? stats.total.toLocaleString() : 0
        },

        logout() {
            this.$store.dispatch(AUTH_LOGOUT, {}).then(resp => {
                this.$router.push({name: 'login'})
            })
        },
        createTeam() {
            this.$modal.show(CreateTeamModal, {
                text: 'This text is passed as a property'
            }, {
                height: 'auto'
            })
        },
        createInvoice() {
            this.$modal.show(CreateInvoiceModal, {
                text: 'This text is passed as a property'
            }, {
                height: 'auto'
            })
        },
        supportModal() {
            this.$modal.show(SupportModal, {
                text: 'This text is passed as a property'
            }, {
                height: 'auto'
            })
        },
        suggestionModal() {
            this.$modal.show(SuggestionModal, {
                text: 'This text is passed as a property'
            }, {
                height: 'auto'
            })
        },

        switchTeam(team) {
            this.prevActiveTeamUuid = this.activeTeamId
            this.setActiveTeam(team)
            this.$router.push({name: this.$route.name, params: {team: team.uuid}})
        },

        openMarketplace() {
            this.$modal.show('automation-center', {type: AutomationTypes.MARKETPLACE})
        },

        openReports() {
            this.$router.push({name: 'reports', params: {page: SETTINGS_DEMO_MARKETSHARE_AWARDS}})
        }
    },
    mounted() {
        this.prevActiveTeamUuid = this.activeTeamId
        this.$bus.$on('OPEN_SUPPORT_HELP_WINDOW', this.supportModal)
    },
    beforeDestroy() {
        this.$bus.$off('OPEN_SUPPORT_HELP_WINDOW')
    },
    watch: {
        hasAllOnboardingStepsPassed(next, prev) {
            if (this.prevActiveTeamUuid === this.activeTeamId && next && !prev) {
                this.isSameSessionWhenOnboardingStepsPassed = true
            }
        }
    }
}
</script>

<style scoped lang="scss">
.dropdown-item {
    color: #000;
}

.teams-list {
    max-height: 160px;
}

.team-name {
    cursor: pointer;
    white-space: nowrap;
}

.invalid-connections-badge,
.unread-notifications-count-badge {
    position: absolute;
    color: white;
    font-weight: 600;
    font-size: 10px;
    line-height: 12px;
    padding: 1px 4px;
    border-radius: 50%;
}

.unread-notifications-count-badge {
    background: $color-purple-light;
    top: -6px;
    right: -12px;
}

.invalid-connections-badge {
    color: $color-danger;
    font-size: 12px;
    top: 0px;
    right: -16px;
}

.menu-icon-container {
    display: inline-block;
    width: 24px;
    font-size: 16px;
    text-align: center;
    margin-right: 4px;
}

.header-setting-btn {
    position: relative;
    display: inline-block;
    line-height: 40px;
    font-size: 28px;
    vertical-align: middle;
    color: $color-purple-light;

    &:hover {
        cursor: pointer;
    }
}
</style>
